import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { typedKeptMessage } from "@/lib/typedAmountKept";   // Step 12 add-on G6.1: one helper for Cancel
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import { Button } from "@/components/ui/button";
import Ref from "@/components/Ref";
import Amount from "@/components/Amount";
import { toast } from "sonner";
import { confirmPossibleDuplicate } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import useMissingFields, { FieldMessage } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import { Plus, Send, AlertTriangle, X } from "lucide-react";
import { ReviewControls, ReviewCountsProvider } from "@/components/reviewpoints/ReviewPointsPanel";   // Batch 28 · item 8 (M54)

const TYPES = ["misstatement", "deficiency", "disclosure", "other"];
const ASSERTIONS = ["E", "C", "A", "V", "CO", "R", "CL", "P"];

const emptyDraft = () => ({
  description: "", type: "misstatement", amount: "",
  assertion: "", wp_ref: "",
});

export default function ObservationsDialog() {
  const colW = useResizableColumns({ storageKey: "observations-dialog", defaults: [110, 240, 110, 130, 160, 160, 110, 100], mins: [72, 130, 72, 90, 100, 100, 72, 60] });   // Step 11d · add-on
  const [sp, setSp] = useSearchParams();
  const openCode = sp.get("openObs") || "";
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);   // P1 — inline form state
  const [busy, setBusy] = useState(false);

  const close = () => {
    const n = new URLSearchParams(sp); n.delete("openObs"); setSp(n);
    setRows([]); setDraft(null); setError(null);
  };
  const load = useCallback(async () => {
    if (!openCode || !currentEngId) return;
    setError(null);
    try {
      const r = await api.get(`/engagements/${currentEngId}/observations/${openCode}`);
      setRows(r.data.rows || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || t("common.failed");
      setError(String(msg));
      toast.error(msg);
    }
  }, [openCode, currentEngId, t]);
  useEffect(() => { load(); }, [load]);
  const mf = useMissingFields(lang);     // Batch 27 · Add-on 1
  if (!openCode) return null;

  const openDraft = () => setDraft(emptyDraft());
  const cancelDraft = () => setDraft(null);
  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.description.trim()) { toast.error(t("obs.new.descRequired")); return; }
    setBusy(true);
    try {
      const body = {
        description: draft.description.trim(),
        type: draft.type,
        amount: draft.amount === "" || draft.amount == null
                  ? null : Number(draft.amount),
        assertion: draft.assertion,
        wp_ref: draft.wp_ref,
      };
      await api.post(`/engagements/${currentEngId}/observations/${openCode}`, body);
      setDraft(null);
      await load();
      toast.success(t("obs.new.created"));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || t("common.failed");
      setError(String(msg));
      if (!mf.apply(e)) toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  const send = async (id, amount) => {
    // Batch 29 · M65: a manual misstatement only WARNS when an active row has the same FS head and amount (±1.00)
    if (!(await confirmPossibleDuplicate({ engId: currentEngId, fsHead: openCode, amount, lang }))) return;
    try {
      await api.post(`/observations/${id}/send-to-misstatements`);
      toast.success(t("obs.sentToMs"));
      load();
    } catch (e) { toast.error(typedKeptMessage(e) || e?.response?.data?.detail || t("common.failed")); }   // Step 12 add-on G6.1
  };
  const patch = async (id, body) => {
    try { await api.patch(`/observations/${id}`, body); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <FullScreenDialog open={true} onClose={close} testid="observations-dialog"
                      title={<span data-testid={`obs-title-${openCode}`}>
            <Ref>{openCode}-05</Ref> {t("obs.title")}
      </span>}>
        <div className="pe-1">
          <div className="mb-2 flex justify-end" data-testid="obs-rp">
            <ReviewCountsProvider engagementId={currentEngId} targetType="observations">
              <ReviewControls engagementId={currentEngId} targetType="observations" targetId={openCode} targetLabel={`${openCode}-05`} compact />
            </ReviewCountsProvider>
          </div>
          <div className="mb-2">
            <Button size="sm" variant="outline" onClick={openDraft} disabled={busy || !!draft} data-testid="obs-add">
              <Plus className="me-1 h-3 w-3" /> {t("obs.addBtn")}
            </Button>
          </div>
          {error && (
            <div className="mb-2 flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
                 data-testid="obs-error">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
              <div>{error}</div>
            </div>
          )}
          {draft && (
            <div className="mb-2 rounded border border-emerald-300 bg-emerald-50/60 p-3 text-[11px] dark:border-emerald-900 dark:bg-emerald-950/20"
                 data-testid="obs-form">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium">{t("obs.new.title")}</div>
                <button type="button" onClick={cancelDraft}
                        className="text-slate-600 hover:text-slate-900 dark:text-slate-300"
                        data-testid="obs-form-close"><X className="h-3 w-3" /></button>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <label className="col-span-1 md:col-span-2 flex flex-col gap-1">
                  <span>{t("obs.col.desc")} *</span>
                  <textarea rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    data-testid="obs-form-desc"
                    className="rounded border px-2 py-1 text-[11px] dark:bg-slate-900" />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t("obs.col.type")}</span>
                  <select value={draft.type}
                    onChange={(e) => { setDraft({ ...draft, type: e.target.value }); mf.filled("type", e.target.value); }}
                    data-testid="obs-form-type"
                    {...mf.props("type", "rounded border px-2 py-1 text-[11px] dark:bg-slate-900")}>
                    {TYPES.map((tp) => (
                      <option key={tp} value={tp}>{t(`obs.type.${tp}`)}</option>
                    ))}
                  </select>
                  <FieldMessage mf={mf} field="type" testid="obs-type-missing" />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t("obs.col.amount")}</span>
                  <input type="number" step="0.01"
                    value={draft.amount}
                    onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                    data-testid="obs-form-amount"
                    className="rounded border px-2 py-1 text-[11px] dark:bg-slate-900" />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t("obs.col.assertion")}</span>
                  <select value={draft.assertion}
                    onChange={(e) => setDraft({ ...draft, assertion: e.target.value })}
                    data-testid="obs-form-assertion"
                    className="rounded border px-2 py-1 text-[11px] dark:bg-slate-900">
                    <option value="">—</option>
                    {ASSERTIONS.map((a) => <option key={a} value={a}>{a} · {t(`assert.${a}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t("obs.col.wp")}</span>
                  <input type="text"
                    value={draft.wp_ref}
                    onChange={(e) => setDraft({ ...draft, wp_ref: e.target.value })}
                    placeholder={t("obs.new.wpPlaceholder")}
                    data-testid="obs-form-wp"
                    className="rounded border px-2 py-1 text-[11px] dark:bg-slate-900" />
                </label>
                <div className="col-span-1 md:col-span-2 flex justify-end gap-2 pt-1">
                  <Button size="sm" variant="ghost" onClick={cancelDraft}
                          data-testid="obs-form-cancel">{t("common.cancel")}</Button>
                  <Button size="sm" onClick={saveDraft} disabled={busy}
                          data-testid="obs-form-save">
                    {busy ? t("common.loading") : t("obs.new.save")}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {rows.length === 0 && !draft
            ? <div className="text-xs text-muted-foreground" data-testid="obs-empty">{t("obs.empty")}</div>
            : rows.length > 0 && (
            <ResizableTable cols={colW} wrap className="w-full text-[11px]">
              <thead className="border-b bg-slate-50 dark:bg-slate-900/40">
                <tr>
                  <ResizableTh index={0} className="px-2 py-1 text-start">{t("obs.col.ref")}</ResizableTh>
                  <ResizableTh index={1} className="px-2 py-1 text-start">{t("obs.col.desc")}</ResizableTh>
                  <ResizableTh index={2} className="px-2 py-1 text-start">{t("obs.col.type")}</ResizableTh>
                  <ResizableTh index={3} className="px-2 py-1 text-end">{t("obs.col.amount")}</ResizableTh>
                  <ResizableTh index={4} className="px-2 py-1 text-start">{t("obs.col.assertion")}</ResizableTh>
                  <ResizableTh index={5} className="px-2 py-1 text-start">{t("obs.col.wp")}</ResizableTh>
                  <ResizableTh index={6} className="px-2 py-1 text-start">{t("obs.col.status")}</ResizableTh>
                  <ResizableTh index={7}></ResizableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`obs-row-${r.ref}`}>
                    <td className="px-2 py-1 font-mono"><Ref>{r.ref}</Ref></td>
                    <td className="px-2 py-1">{r.description}</td>
                    <td className="px-2 py-1">
                      <span data-testid={`obs-type-${r.ref}`}>{t(`obs.type.${r.type}`)}</span>
                    </td>
                    <td className="px-2 py-1 text-end"><Amount value={r.amount} /></td>
                    <td className="px-2 py-1">{r.assertion || ""}</td>
                    <td className="px-2 py-1">{r.wp_ref || ""}</td>
                    <td className="px-2 py-1"><span className="rounded bg-slate-100 px-1 text-[9px] dark:bg-slate-800"
                                                        data-testid={`obs-status-${r.ref}`}>{t(`obs.status.${r.status}`)}</span></td>
                    <td className="px-2 py-1 text-end">
                      {(r.type === "misstatement" || r.type === "disclosure") && r.status !== "sent" && (   /* Batch 31 · item 10 (M92) */
                        <Button size="sm" variant="outline" onClick={() => send(r.id, r.amount)}
                                data-testid={`obs-send-${r.ref}`}>
                          <Send className="me-1 h-3 w-3" /> {t("obs.sendMs")}
                        </Button>
                      )}
                      {r.status !== "cleared" && r.status !== "sent" && (
                        <button className="ms-1 text-[10px] text-emerald-700 underline"
                                onClick={() => patch(r.id, { status: "cleared" })}
                                data-testid={`obs-clear-${r.ref}`}>{t("obs.status.cleared")}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          )}
        </div>
    </FullScreenDialog>
  );
}
